import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "../../components/ui/dialog";
import { DOC_PRESETS, TRIM_SIZES } from "./tools-config";
import { X, BookOpen, Sparkles, Upload, FileText, BookCopy, Wand2 } from "lucide-react";
import { cn } from "../../lib/utils";

const PRESET_ICON = {
  recent: BookOpen, saved: BookCopy, print: FileText, ebook: BookOpen,
  workbook: FileText, "ai-start": Sparkles, upload: Upload,
};

export default function NewBookDocumentModal({ open, onOpenChange, initialPreset = "print" }) {
  const nav = useNavigate();
  const [preset, setPreset] = useState(initialPreset);
  const [trim, setTrim] = useState("6x9");
  const trimCfg = TRIM_SIZES.find((t) => t.id === trim);

  const [form, setForm] = useState({
    title: "Untitled Book",
    author: "Founder",
    intent: "Print",
    units: "Inches",
    pages: 24,
    facing: true,
    primaryFrame: true,
    columns: 1,
    gutter: 0.17,
    mTop: 0.75, mBottom: 0.75, mInside: 0.75, mOutside: 0.5,
    bTop: 0.125, bBottom: 0.125, sTop: 0.125, sBottom: 0.125,
    preview: true,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = () => {
    const params = new URLSearchParams({
      title: form.title,
      author: form.author,
      trim,
      pages: String(form.pages),
      preset,
    });
    onOpenChange(false);
    nav(`/book-lab/editor?${params.toString()}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="new-book-modal"
        className="max-w-[1080px] w-[96vw] p-0 border-[var(--pl-border)] bg-[var(--pl-surface)]/95 backdrop-blur-xl overflow-hidden gap-0"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--pl-border)]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">BookLab · Editor Studio</div>
            <div className="font-display text-base font-semibold text-[var(--pl-text)]">New Book Document</div>
          </div>
          <button
            data-testid="new-book-close"
            onClick={() => onOpenChange(false)}
            className="h-8 w-8 grid place-items-center rounded-lg text-[var(--pl-text-muted)] hover:text-rose-400"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] max-h-[78vh] md:max-h-[80vh] overflow-hidden">
          {/* Left categories */}
          <aside className="border-b md:border-b-0 md:border-r border-[var(--pl-border)] p-3 overflow-y-auto pl-noscroll">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-text-muted)] px-2 py-1">Templates</div>
            <div className="grid grid-cols-2 md:grid-cols-1 gap-1 mt-1">
              {DOC_PRESETS.map((p) => {
                const I = PRESET_ICON[p.id] || FileText;
                return (
                  <button
                    key={p.id}
                    data-testid={`preset-${p.id}`}
                    onClick={() => setPreset(p.id)}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition",
                      preset === p.id
                        ? "bg-[var(--pl-aqua)]/15 text-[var(--pl-aqua)]"
                        : "text-[var(--pl-text-muted)] hover:text-[var(--pl-text)] hover:bg-white/5"
                    )}
                  >
                    <I className="h-4 w-4" /> {p.label}
                  </button>
                );
              })}
            </div>
          </aside>

          {/* Right preset form */}
          <div className="overflow-y-auto p-5 space-y-4">
            {/* Hero preview row */}
            <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-4">
              <div
                data-testid="modal-preview"
                className="aspect-[6/9] rounded-xl border border-[var(--pl-border)] grid place-items-center text-center p-3"
                style={{ background: "linear-gradient(180deg, #ffffff, #f4faf8)" }}
              >
                <div>
                  <div className="font-display text-[13px] text-[#071c1c] font-semibold leading-tight">{form.title}</div>
                  <div className="text-[10px] text-[#526967] mt-1.5 uppercase tracking-[0.18em]">{form.author}</div>
                  <div className="mt-3 text-[9px] text-[#526967]">{trimCfg.label} · {form.pages} pp</div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Book title"><Input testId="m-title" value={form.title} onChange={(v) => set("title", v)} /></Field>
                <Field label="Author name"><Input testId="m-author" value={form.author} onChange={(v) => set("author", v)} /></Field>
                <Field label="Intent">
                  <Select testId="m-intent" value={form.intent} onChange={(v) => set("intent", v)}
                    options={["Print", "Ebook", "Workbook", "Comic", "Custom"]} />
                </Field>
                <Field label="Trim size">
                  <Select testId="m-trim" value={trim} onChange={setTrim}
                    options={TRIM_SIZES.map((t) => ({ value: t.id, label: t.label }))} />
                </Field>
                <Field label="Width"><Input testId="m-width" type="number" value={trimCfg.width} readOnly /></Field>
                <Field label="Height"><Input testId="m-height" type="number" value={trimCfg.height} readOnly /></Field>
                <Field label="Units">
                  <Select testId="m-units" value={form.units} onChange={(v) => set("units", v)}
                    options={["Inches", "Pixels", "Points", "Picas", "Millimeters"]} />
                </Field>
                <Field label="Pages"><Input testId="m-pages" type="number" value={form.pages} onChange={(v) => set("pages", Number(v))} /></Field>
              </div>
            </div>

            <Toggle testId="m-facing" label="Facing pages" value={form.facing} onChange={(v) => set("facing", v)} />
            <Toggle testId="m-primary-frame" label="Primary text frame" value={form.primaryFrame} onChange={(v) => set("primaryFrame", v)} />

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Field label="Columns"><Input testId="m-cols" type="number" value={form.columns} onChange={(v) => set("columns", Number(v))} /></Field>
              <Field label="Column gutter"><Input testId="m-gutter" type="number" step="0.05" value={form.gutter} onChange={(v) => set("gutter", Number(v))} /></Field>
            </div>

            <Section title="Margins">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Field label="Top"><Input type="number" step="0.05" value={form.mTop} onChange={(v) => set("mTop", Number(v))} /></Field>
                <Field label="Bottom"><Input type="number" step="0.05" value={form.mBottom} onChange={(v) => set("mBottom", Number(v))} /></Field>
                <Field label="Inside"><Input type="number" step="0.05" value={form.mInside} onChange={(v) => set("mInside", Number(v))} /></Field>
                <Field label="Outside"><Input type="number" step="0.05" value={form.mOutside} onChange={(v) => set("mOutside", Number(v))} /></Field>
              </div>
            </Section>

            <Section title="Bleed & Slug">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Field label="Bleed top"><Input type="number" step="0.05" value={form.bTop} onChange={(v) => set("bTop", Number(v))} /></Field>
                <Field label="Bleed bottom"><Input type="number" step="0.05" value={form.bBottom} onChange={(v) => set("bBottom", Number(v))} /></Field>
                <Field label="Slug top"><Input type="number" step="0.05" value={form.sTop} onChange={(v) => set("sTop", Number(v))} /></Field>
                <Field label="Slug bottom"><Input type="number" step="0.05" value={form.sBottom} onChange={(v) => set("sBottom", Number(v))} /></Field>
              </div>
            </Section>
          </div>
        </div>

        {/* Sticky footer */}
        <div className="border-t border-[var(--pl-border)] px-5 py-3 flex items-center justify-between gap-3 bg-[var(--pl-surface)]/80 backdrop-blur sticky bottom-0">
          <label className="flex items-center gap-2 text-xs text-[var(--pl-text-muted)]">
            <input
              data-testid="m-preview"
              type="checkbox"
              checked={form.preview}
              onChange={(e) => set("preview", e.target.checked)}
              className="accent-[var(--pl-aqua)]"
            />
            Preview
          </label>
          <div className="flex gap-2">
            <button
              data-testid="m-cancel"
              onClick={() => onOpenChange(false)}
              className="h-10 px-4 rounded-xl border border-[var(--pl-border)] text-xs text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40"
            >
              Cancel
            </button>
            <button
              data-testid="m-create"
              onClick={create}
              className="h-10 px-5 rounded-xl pl-btn-aqua text-xs inline-flex items-center gap-2"
            >
              <Wand2 className="h-3.5 w-3.5" /> Create
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-1">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)]">{label}</div>
      {children}
    </label>
  );
}

function Input({ testId, type = "text", value, onChange, readOnly, step }) {
  return (
    <input
      data-testid={testId}
      type={type}
      value={value}
      step={step}
      readOnly={readOnly}
      onChange={(e) => onChange?.(e.target.value)}
      style={{ background: "color-mix(in srgb, var(--pl-surface) 70%, transparent)", color: "var(--pl-text)" }}
      className="w-full rounded-lg border border-[var(--pl-border)] px-2.5 h-9 text-xs focus:outline-none focus:border-[var(--pl-aqua)]/50"
    />
  );
}

function Select({ testId, value, onChange, options }) {
  return (
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ background: "color-mix(in srgb, var(--pl-surface) 70%, transparent)", color: "var(--pl-text)" }}
      className="w-full rounded-lg border border-[var(--pl-border)] px-2.5 h-9 text-xs focus:outline-none focus:border-[var(--pl-aqua)]/50"
    >
      {options.map((o) =>
        typeof o === "string" ? <option key={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>
      )}
    </select>
  );
}

function Toggle({ testId, label, value, onChange }) {
  return (
    <label className="flex items-center gap-2 text-xs text-[var(--pl-text)]">
      <input
        data-testid={testId}
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-[var(--pl-aqua)]"
      />
      {label}
    </label>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)] mb-2">{title}</div>
      {children}
    </div>
  );
}
